// One-stop explainer for the growth screen — what SDS / percentile /
// AGA-SGA-LGA / each reference standard / each msgcode mean. Surfaced
// from the (?) in the HeaderBar. Aims to read like a brief paediatrician
// would explain it to a curious parent.

import { Icon } from '../Icon.tsx';
import { SheetShell } from '../sheets/SheetShell.tsx';

type Props = { open: boolean; onClose: () => void };

export function GrowthAboutSheet({ open, onClose }: Props) {
  return (
    <SheetShell
      open={open}
      onClose={onClose}
      title="About growth metrics"
      tagline="What the numbers mean"
      accent="accent"
      icon={(p) => <Icon.info {...p} />}
      secondaryLabel="Close"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18, fontSize: 14, lineHeight: 1.45 }}>
        <Section title="SDS (Standard Deviation Score, “z-score”)">
          A measurement expressed in units of standard deviation from the
          population median at the same age and sex.
          <ul style={ulStyle}>
            <li>SDS 0 — exactly median</li>
            <li>SDS +1 — one SD above median (≈ 84th percentile)</li>
            <li>SDS −2 — two SDs below (≈ 2nd percentile)</li>
          </ul>
          Roughly 95% of healthy children fall between −2 and +2. Outside
          that range most paediatric guidelines (WHO, Dutch JGZ, others)
          recommend taking a closer look.
        </Section>

        <Section title="Percentile (P)">
          The percentage of same-age, same-sex peers below this value.{' '}
          <Mono>P50</Mono> is the median, <Mono>P3</Mono> ≈ −2 SD,{' '}
          <Mono>P97</Mono> ≈ +2 SD. Always computed from the SDS via the
          standard-normal CDF, so percentile and SDS are two views of the
          same number.
        </Section>

        <Section title="AGA · SGA · LGA (at birth)">
          International classification used by paediatricians for the very
          first weight (and length) measurement:
          <ul style={ulStyle}>
            <li><b>AGA</b> — Appropriate for Gestational Age (−2 ≤ z ≤ +2)</li>
            <li><b>SGA</b> — Small for Gestational Age (z &lt; −2)</li>
            <li><b>LGA</b> — Large for Gestational Age (z &gt; +2)</li>
          </ul>
          We classify using the Dutch SGA reference (Visser 2009), indexed
          by sex and <i>completed weeks of gestation</i> — which is why we
          ask for the due date when you add a baby. The cutoffs themselves
          (±2 SD) are international.
        </Section>

        <Section title="Reference curves">
          The chart bands and SDS values come from whichever reference you
          pick in Settings → Growth & curves. There’s no universally
          “correct” choice — pick the one your paediatrician uses:
          <ul style={ulStyle}>
            <li><b>WHO (2006)</b> — WHO Child Growth Standards. International, healthy breastfed reference. Reasonable default if you’re outside the Netherlands.</li>
            <li><b>NL5 (2009, Schönbeck)</b> — Current Dutch standard; what the Dutch JGZ uses.</li>
            <li><b>NL4 (1997, Fredriks)</b> — Previous Dutch reference.</li>
            <li><b>NL3 (1980, Roede & van Wieringen)</b> — Older Dutch reference. Weight only.</li>
          </ul>
          Automatic fallbacks the app applies:
          <ul style={ulStyle}>
            <li>Head circumference: every Dutch reference falls back to <Mono>NL1997</Mono> (no newer Dutch HC reference exists). WHO has its own HC curves.</li>
            <li>NL3 length falls back to NL4 (NL3 didn’t publish length-for-age).</li>
            <li>Preterm (&lt; 37 weeks gestation): weight uses the <b>NL2012 preterm</b> table (Bocca-Tjeertes), indexed by gestational age week.</li>
          </ul>
          <em style={{ color: 'var(--text-3)', fontSize: 12, display: 'block', marginTop: 6 }}>
            Quirk worth knowing (Dutch users): the printed JGZ summary
            computes the SDS <i>column</i> against NL3 even though the
            chart <i>bands</i> on the same paper are NL5. We use one
            reference for both so the number and the chart agree — so our
            SDS may differ slightly from the JGZ printout’s SDS column.
          </em>
        </Section>

        <Section title="Alerts">
          Alerts only appear when the trend looks non-normal. The thresholds
          mirror the Dutch JGZ decision trees (ported from the{' '}
          <Mono>growthscreener</Mono> R package). The numeric{' '}
          <Mono>msgcode</Mono> lets a clinician look up the exact rule:
          <ul style={ulStyle}>
            <li><Mono>2074</Mono> — Underweight (SDS &lt; −2)</li>
            <li><Mono>2075</Mono> — Strong weight drop (≥ 1 SD vs an earlier reading)</li>
            <li><Mono>3041</Mono> / <Mono>3043</Mono> — Macrocephaly / microcephaly suspicion (head circumference)</li>
            <li><Mono>3042</Mono> / <Mono>3044</Mono> / <Mono>3045</Mono> — Rapid head-circumference jump or drop</li>
            <li><Mono>1044</Mono> / <Mono>1045</Mono> / <Mono>1048</Mono> — Short / very short / very tall</li>
            <li><Mono>1077</Mono> — Length above +1 SD at this age — monitor</li>
          </ul>
          “No referral indicated” (msgcode 2031 / 3031 / 1031) is the silent
          case — no card shown. To avoid mixing thresholds and references
          alerts only fire when NL5 is selected; the SDS strip on each
          card always follows your chosen reference.
        </Section>

        <Section title="Target adult height">
          A prediction of the baby’s final adult height from the biological
          parents’ heights, using the TNO formula (Hermanussen 2010,
          calibrated on Dutch data). The ±10 cm 95% range reflects normal
          genetic and environmental variance — most kids land inside it,
          some don’t. Calibrated for European populations; treat as a
          rough estimate elsewhere.
        </Section>

        <Section title="Sources">
          <ul style={ulStyle}>
            <li>WHO Multicentre Growth Reference Study Group, <i>Acta Paediatr Suppl</i> 2006 — WHO Child Growth Standards.</li>
            <li>Schönbeck Y, et al. <i>PLoS One</i> 2011 — NL5 weight & length.</li>
            <li>Fredriks AM, et al. <i>Pediatric Research</i> 2000 — NL4 reference.</li>
            <li>Bocca-Tjeertes IFA, et al. <i>J Pediatr</i> 2012 — Preterm reference.</li>
            <li>Dutch JGZ guidelines (Ondergewicht 2019, Overgewicht 2012, Lengtegroei 2019) — alert thresholds.</li>
            <li><Mono>growthcharts/growthscreener</Mono> — decision-tree port source.</li>
          </ul>
        </Section>
      </div>
    </SheetShell>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--text-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.04,
          margin: '0 0 8px',
        }}
      >
        {title}
      </h3>
      <div style={{ color: 'var(--text-1)' }}>{children}</div>
    </section>
  );
}

function Mono({ children }: { children: React.ReactNode }) {
  return (
    <code
      className="lt-mono lt-tabular"
      style={{
        background: 'var(--bg-sunken)',
        padding: '1px 5px',
        borderRadius: 5,
        fontSize: 12,
      }}
    >
      {children}
    </code>
  );
}

const ulStyle: React.CSSProperties = {
  margin: '6px 0 6px 0',
  paddingLeft: 18,
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
};
